import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { useDeleteLabel, useLabels, useLabelUsage, useMergeLabels, useUpdateLabel } from "../lib/labels";
import { LABEL_COLORS, type LabelColor } from "../../shared/schemas/labels";
import type { PublicLabel } from "../../shared/types/projects";
import { Card, CardContent, CardDescription, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ConfirmDialog } from "../components/workspaces/ConfirmDialog";
import { LabelChip } from "../components/issues/IssueFields";
import { strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { AdministratorsOnly, QueryGate } from "../components/ui/states";
import { Skeleton } from "../components/ui/skeleton";

const words = strings.labels;
const SELECT =
  "h-9 rounded-md border border-[hsl(var(--input))] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:outline-none";

const failed = (err: unknown) => toast.error(err instanceof ApiError && err.status < 500 ? err.message : words.failed);

function LabelRow({
  label,
  count,
  others,
  onDelete,
}: {
  label: PublicLabel;
  /** How many issues carry it, once counted. */
  count: number | undefined;
  others: PublicLabel[];
  onDelete: () => void;
}) {
  const update = useUpdateLabel();
  const merge = useMergeLabels();
  const [name, setName] = useState(label.name);
  const [was, setWas] = useState(label.name);
  if (was !== label.name) {
    setWas(label.name);
    setName(label.name);
  }
  const rename = () => {
    const next = name.trim();
    if (next !== "" && next !== label.name)
      update.mutate({ id: label.id, patch: { name: next } }, { onError: (e) => (failed(e), setName(label.name)) });
    else setName(label.name);
  };
  return (
    <li className="flex flex-wrap items-center gap-2 py-2">
      <LabelChip name={label.name} color={label.color} />
      <Input
        aria-label={words.nameLabel.replace("{name}", label.name)}
        value={name}
        maxLength={50}
        onChange={(e) => setName(e.target.value)}
        onBlur={rename}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            rename();
          }
        }}
        className="h-9 min-w-32 flex-1"
      />
      <select
        aria-label={words.colorLabel.replace("{name}", label.name)}
        className={SELECT}
        value={label.color}
        onChange={(e) =>
          update.mutate({ id: label.id, patch: { color: e.target.value as LabelColor } }, { onError: failed })
        }
      >
        {LABEL_COLORS.map((color) => (
          <option key={color} value={color}>
            {strings.projects.settings.colors[color]}
          </option>
        ))}
      </select>
      <span className="w-20 text-xs text-[hsl(var(--muted-foreground))]">
        {count === undefined ? words.usesUnknown : words.uses.replace("{count}", String(count))}
      </span>
      {others.length > 0 && (
        <select
          aria-label={words.mergeLabel.replace("{name}", label.name)}
          className={SELECT}
          value=""
          disabled={merge.isPending}
          onChange={(e) =>
            e.target.value !== "" &&
            merge.mutate(
              { id: label.id, targetId: e.target.value },
              { onSuccess: () => toast.success(words.merged.replace("{name}", label.name)), onError: failed },
            )
          }
        >
          <option value="">{words.mergeInto}</option>
          {others.map((other) => (
            <option key={other.id} value={other.id}>
              {other.name}
            </option>
          ))}
        </select>
      )}
      <Button size="sm" variant="ghost" className="text-[hsl(var(--destructive-text))]" onClick={onDelete}>
        {words.delete}
      </Button>
    </li>
  );
}

/**
 * The workspace's labels, for Steer's Administrators (BUILD_SPEC §10.7): renamed, recoloured, merged into another and
 * deleted, each change made on every issue carrying them and recorded in its history. Contributors make labels as they
 * type them on an issue.
 */
function LabelsRoute() {
  const slug = workspaceSlug();
  const view = useWorkspace(slug);
  const administrator = view.data?.role === "administrator";
  const labels = useLabels();
  const usage = useLabelUsage(administrator);
  const remove = useDeleteLabel();
  const [deleting, setDeleting] = useState<PublicLabel | null>(null);

  if (view.isLoading)
    return (
      <div className="mx-auto max-w-3xl space-y-3 py-12">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (view.error instanceof ApiError || !view.data) return <ToWorkspaces />;
  if (!administrator) return <AdministratorsOnly what={strings.states.adminPages.labels} />;
  // Each label's issues, once counted: never a count of none before then.
  const countOf = (id: string) => (usage.data === undefined ? undefined : (usage.data[id] ?? 0));
  const deletingCount = deleting === null ? undefined : countOf(deleting.id);

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-12">
      <Card>
        <CardHeader>
          <h1 className="text-xl leading-none font-semibold tracking-tight">{words.title}</h1>
          <CardDescription>{words.body}</CardDescription>
        </CardHeader>
        <CardContent>
          <QueryGate query={labels} what={words.theLabels} skeleton={<Skeleton className="h-24 w-full" />}>
            {(all) =>
              all.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.empty}</p>
              ) : (
                <ul className="divide-y divide-[hsl(var(--border))]">
                  {all.map((label) => (
                    <LabelRow
                      key={label.id}
                      label={label}
                      count={countOf(label.id)}
                      others={all.filter((other) => other.id !== label.id)}
                      onDelete={() => setDeleting(label)}
                    />
                  ))}
                </ul>
              )
            }
          </QueryGate>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={words.deleteTitle.replace("{name}", deleting?.name ?? "")}
        description={
          deletingCount === undefined
            ? words.deleteBodyUncounted
            : words.deleteBody.replace("{count}", String(deletingCount))
        }
        confirmLabel={words.delete}
        destructive
        busy={remove.isPending}
        onConfirm={() =>
          deleting !== null && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null), onError: failed })
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/admin/settings/labels")({ component: LabelsRoute });
